<template>
  <div
    class="chess-table-fixed-cell"
    :class="`chess-table-fixed-cell--vertical-align-${props.fixed.verticalAlign || 'start'}`"
  >
    <div>
      <e-tooltip v-if="props.fixed.title" location="bottom" :disabled="!isTitleTruncated" :max-width="364">
        <template #activator>
          <b ref="titleRef" v-html="props.fixed.title" />
        </template>
        <div class="paragraph-xs font-600 text-secondary" v-html="props.fixed.title" />
      </e-tooltip>
      <div v-if="props.fixed.text" v-html="props.fixed.text" />
    </div>

    <e-tooltip v-if="props.fixed.tooltip || slots.tooltip" location="right">
      <template #activator="{ props }">
        <div v-bind="props">
          <v-icon class="text-ordinary"> mdi-help-circle-outline</v-icon>
        </div>
      </template>
      <div v-html="props.fixed.tooltip" />
      <slot name="tooltip" />
    </e-tooltip>
  </div>
</template>

<script setup lang="ts" generic="K">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import type { ChessTableRowFixedType } from '@/core/components/interface/Chess/Chess.type';
import { ETooltip } from '@/core/components/ui';

const props = defineProps<{
  fixed: ChessTableRowFixedType<K>;
}>();

const slots = defineSlots<{
  tooltip?: (props: unknown) => void;
}>();

const titleRef = ref<HTMLElement>();
const isTitleTruncated = ref(false);

let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;

onMounted(() => {
  if (titleRef.value) {
    // ResizeObserver следит за изменением размеров контейнера
    resizeObserver = new ResizeObserver(checkTitleTruncation);
    resizeObserver.observe(titleRef.value);

    // MutationObserver следит за изменениями текста
    mutationObserver = new MutationObserver(checkTitleTruncation);
    mutationObserver.observe(titleRef.value, { childList: true, characterData: true, subtree: true });

    checkTitleTruncation();
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
});

const checkTitleTruncation = () => {
  if (titleRef.value) {
    isTitleTruncated.value = titleRef.value.scrollWidth > titleRef.value.clientWidth;
  }
};
</script>

<style scoped lang="scss">
.chess-table-fixed-cell {
  display: grid;

  grid-template-columns: minmax(0, 1fr) auto;

  height: 100%;
  padding: 8px;

  background-color: $body-bg-color;
  border-radius: inherit;

  b {
    @include ellipsis();

    margin-bottom: 4px;

    font-size: 10px;
    line-height: 12px;
    font-weight: 600;
    color: $primary-color;
  }

  &--vertical-align-start {
    align-items: flex-start;
  }

  &--vertical-align-center {
    align-items: center;
  }

  &--vertical-align-end {
    align-items: flex-end;
  }
}
</style>
